import { extractMonthDays, extractUncertainDates } from "./figures";
import type { AppointmentStatus } from "./types";
import { DAY_MS, HOURS } from "./window";

/**
 * Loop item V8: the occasion planner. The canvas's Shepherd and Campaign
 * Operator boxes: "an engagement ring automatically sets wedding bands at 6 to
 * 12 months, an anniversary gift every year, and upgrade conversations at the
 * 5 and 10 year marks", and "bad dates are worse than no dates ... every touch
 * stays approval-gated, draft-only".
 *
 * PURE AND PRINT-ONLY. A contact's dated record facts and a now go in; proposed
 * touch dates come out, each with its play and the record ids it cites. No
 * database, no clock, no model call. Nothing here writes a row, sends, queues
 * or drafts anything, and a proposal carries no wording, no price and no
 * discount (T3): its fields are a play name, two dates, ids and a state. The
 * loader is `packages/db/src/callList.ts`; the callers are `leadQuality.ts`
 * (the call list's `occasion_within_30d` signal and its timeline reading) and
 * `pnpm leads occasions`.
 *
 * EVERY INTERVAL AND WINDOW BELOW IS A NAMED CONSTANT, AWAITING RATIFICATION
 * (Kabir). They are the canvas's own words where it gives numbers, and the
 * calendar windows are this file's provisional reading where it gives none.
 * `provisionalSummary()` prints them all, so a report never shows a date
 * without the rule that made it.
 *
 * DATES COME ONLY FROM THE RECORD:
 *  - an outcome labelled `sold` or `deposit` (its `recorded_at`);
 *  - a TA-507 key-date memory row: content "<label>: <Mon D>", evidence kind
 *    `dm.customer-claim`, live (active) status. The row carries no year, so a
 *    year is never guessed: a wedding date is never inferred, and the only key
 *    dates that plan anything are the customer's own anniversary and a stated
 *    birthday (their own or a named partner's), which recur yearly whatever
 *    their first year was;
 *  - an appointment, which only annotates a touch (`onCalendar`) and never
 *    anchors one or suppresses one.
 * A held key date is listed as held and never planned from. A fact with no
 * readable date plans nothing and is listed as undated. An occasion is never
 * inferred from message text, and the owner's VIP tag is not an input (F-11).
 *
 * A CONTACT ON THE SEND SUPPRESSION LIST (`send_suppressions`, section 10.1:
 * bereavement, an opt-out, a sensitive life change, "beats every priority")
 * IS PLANNED NOTHING. The loader hands over that a row exists and never its
 * reason, which is the most sensitive text in the table; the plan says only
 * that the contact is suppressed. An anniversary gift proposed to someone in
 * mourning is exactly the bad date the canvas warns of.
 *
 * DAYS ARE THE SHOP'S CALENDAR DAYS (G19). Given the shop's zone
 * (`clients.timezone`), today, a sale's day and an appointment's day are read
 * on the shop's calendar; with no zone they are UTC days, as before. Either
 * way a day is carried as midnight UTC of that calendar date (a date value,
 * not an instant), and a touch spans whole days, first day and last day
 * inclusive. Month arithmetic clamps to the month's last day (Aug 31 plus 6
 * months is Feb 28), never rolls into the next month.
 *
 * WHAT THE PLANNER READS AS A RING SALE. Outcomes carry no product, so the only
 * structural marker is a POSITIVE one: a `routing.decided` on the sale's thread,
 * at or before the sale, whose matched rules include an engagement-ring phrase
 * (`RING_MARKER_RULE_IDS`, or `RING_MARKER_PHRASES` on a row written before
 * design Q20; `routedAsEngagementRing`). The routed scenario alone
 * (`custom_engagement`, `RING_MARKER_SCENARIO`) is NOT a marker: it is a regex
 * over the customer's words that also covers custom pendants, wedding band sets,
 * resets and design requests, and the owner's inquiry type is recorded only when
 * he types it (`pnpm outcomes record --inquiry-type`, sheet item 146; a chip tap
 * writes none), so the veto cannot be relied on to catch them.
 * The marker is still weak (the sale carries no product, and "engagement ring"
 * can be said of a repair or a reset), so it is cited on every touch it supports.
 * A sale without it plans nothing and says so. Which routing event qualifies is
 * the loader's reading (`loadLeadRecords`) and the caller's (`ringMarkerFor` in
 * `leadQuality.ts`); this file takes the event id or null.
 *
 * ONE SET OF PROPOSALS PER CONTACT. Threads are permanent per contact and
 * channel, and a contact may have several, so every later sale or deposit would
 * otherwise start a fresh wedding bands window and fresh 5 and 10 year marks (a
 * repair sold two years after the ring is not a second engagement). The earliest
 * qualifying SALE of the contact carries every play, or the earliest qualifying
 * DEPOSIT when there is no sale; every other qualifying sale or deposit is listed
 * as absorbed by it, with both rows cited. The record cannot say which of two
 * sales was the ring, so the earliest is the conservative anchor.
 */

export const OCCASION_RATIFICATION = "AWAITING RATIFICATION";

/** Wedding bands: from this many months after the sale or deposit, through this many. */
export const WEDDING_BANDS_WINDOW_MONTHS = { from: 6, through: 12 } as const;

/** Upgrade conversations: this many years after a ring SALE (a deposit is not a sale). */
export const UPGRADE_MARK_YEARS: readonly number[] = [5, 10];

/**
 * The routed scenario that marks a thread as an engagement-ring thread. Spelled
 * here, as the other loaders spell it, because it is a stored string.
 */
export const RING_MARKER_SCENARIO = "custom_engagement";

/**
 * The matched phrases (`routing.decided` payload, `matched`) that make a routing
 * an engagement-ring marker, compared trimmed and in lower case. Only these two:
 * "make me", "design a", "custom", "wedding band set", "reset my diamond" and the
 * rest of the router's custom_engagement phrases name other work, and "proposal"
 * is as often a business proposal as a proposal of marriage. PROVISIONAL,
 * AWAITING RATIFICATION (Kabir): which phrases count is his.
 *
 * Only a row written before design Q20 holds these phrases in `matched`; a newer
 * row holds `RING_MARKER_RULE_IDS` there instead.
 */
export const RING_MARKER_PHRASES: readonly string[] = ["engagement ring", "propose"];

/**
 * The router's rule ids for the same two phrases (design row Q20, decision D31,
 * docs/DESIGN-learn-qualify-2026-10-03.md). `routing.decided` records rule ids
 * since Q20, never the words, so a new row names "engagement ring" as
 * `custom_engagement.occasion.engagement_ring`. Derived from
 * `RING_MARKER_PHRASES`, so the phrase list stays the one list to ratify; the
 * test holds that every derived id is one the router can record.
 */
export const RING_MARKER_RULE_IDS: readonly string[] = RING_MARKER_PHRASES.map(
  (p) => `custom_engagement.occasion.${p.replace(/ /g, "_")}`,
);

/**
 * Whether a routing's `matched` payload names an engagement ring. Fails closed:
 * anything that is not a list, and any entry that is not a string, reads false.
 * The one place the allowlist is applied; the loader hands over only the event id.
 *
 * Two shapes, one answer (design Q20): a row written since rule ids holds ids,
 * compared exactly; a row written before holds the matched words, compared
 * trimmed and in lower case as they always were. The legacy branch answers only
 * whether a phrase is on the list and never returns, logs or prints the words.
 * A route records one id per pattern, for the alternative that matched first,
 * so "wedding band set and an engagement ring" is not a marker on either shape,
 * exactly as before.
 */
export function routedAsEngagementRing(matched: unknown): boolean {
  if (!Array.isArray(matched)) return false;
  return matched.some(
    (m) =>
      typeof m === "string" &&
      (RING_MARKER_RULE_IDS.includes(m) || RING_MARKER_PHRASES.includes(m.trim().toLowerCase())),
  );
}

/** The one key-date label that plans a yearly touch: the customer's own anniversary ("our anniversary"). */
export const ANNIVERSARY_LABEL = "anniversary";

/**
 * The key-date labels that plan a yearly birthday touch (TA-1196, vault Planning
 * elements item 7, "birthday reactivation"): a birthday the customer stated for
 * themselves ("my birthday", stored `birthday (customer)`) or for a named partner
 * (`birthday (her)`, `(him)`, `(them)`). Only a `dm.customer-claim` row is ever
 * read (`parseKeyDateRow`), so a date from any other source plans nothing.
 */
export const BIRTHDAY_LABELS: readonly string[] = ["birthday", "birthday (customer)", "birthday (her)", "birthday (him)", "birthday (them)"];

/**
 * Birthday touch: this many days before the stated date the touch opens, and it
 * runs through the date. G9: 7 is TA-1196's recorded rec ("7 days, so a gift can
 * still be ordered"), not overruled; a lead time is cadence (T1, MS B5). The
 * touch id names the date itself, so a later change of the lead never makes a
 * second proposal for the same birthday. PROVISIONAL (Kabir): the figure is his.
 */
// AWAITING RATIFICATION
export const BIRTHDAY_LEAD_DAYS = 7;

/**
 * G18 (IC 6: "the pipeline shepherd's plays (birthday, 11-month recall,
 * stale-lead nudge)"; AB 5.5 L5): a past buyer is recalled this many months
 * after the contact's latest standing sale, on that one day. Only for a contact
 * the ring plays do not plan for (one set of proposals per contact), cancelled
 * by any lost the contact has recorded after that sale. PROVISIONAL (Kabir).
 */
// AWAITING RATIFICATION
export const PURCHASE_RECALL_MONTHS = 11;

/**
 * How far ahead `nextTouch` is asked to look for the call list: 11 years, so
 * the 10 year mark of a sale made today is inside it. The CLI's `--days`
 * narrows what it prints; this is only how far the call list may see.
 */
export const NEXT_OCCASION_REACH_DAYS = 4018;

/** A fixed month (1 to 12) and day. */
export interface WaveMonthDay {
  readonly month: number;
  readonly day: number;
}

/** The `nth` `weekday` (0 is Sunday) of a month, computed each year: Mother's Day is the 2nd Sunday of May. */
export interface WaveNthWeekday {
  readonly month: number;
  readonly weekday: number;
  readonly nth: number;
}

export interface SeasonalWave {
  readonly key: "valentines" | "december" | "engagement_season" | "mothers_day";
  readonly label: string;
  /**
   * A fixed month-day (a window whose `through` month is before its `from`
   * month wraps the new year), or this many days before the computed `through`.
   */
  readonly from: WaveMonthDay | { readonly daysBefore: number };
  readonly through: WaveMonthDay | WaveNthWeekday;
}

/**
 * G39 (AB skill row 7: "Engagement season, V-day, Mother's Day, December"): the
 * Mother's Day wave opens this many days before the day itself. PROVISIONAL
 * (Kabir), like the other three windows.
 */
// AWAITING RATIFICATION
export const MOTHERS_DAY_WINDOW_DAYS = 21;

/**
 * Seasonal waves. A CALENDAR OF WINDOWS ONLY: no person is attached to a wave,
 * and nothing here proposes a touch for one (the Campaign Operator owns waves;
 * a one-to-many marketing send is not a 1:1 route). Provisional bounds, every
 * one AWAITING RATIFICATION; the canvas names three waves and no dates, and the
 * Agent Brains skill row names Mother's Day as the fourth (G39), dated the
 * second Sunday of May (the US date). Push presents and cultural dates are not
 * waves.
 */
export const SEASONAL_WAVES: readonly SeasonalWave[] = [
  { key: "valentines", label: "Valentine's", from: { month: 1, day: 15 }, through: { month: 2, day: 14 } },
  { key: "december", label: "December", from: { month: 12, day: 1 }, through: { month: 12, day: 31 } },
  { key: "engagement_season", label: "engagement season", from: { month: 11, day: 1 }, through: { month: 2, day: 14 } },
  { key: "mothers_day", label: "Mother's Day", from: { daysBefore: MOTHERS_DAY_WINDOW_DAYS }, through: { month: 5, weekday: 0, nth: 2 } },
];

/** The play names a seasonal wave owns. No per-person touch may carry one. */
export const OCCASION_PLAYS_NEVER_PER_PERSON: readonly string[] = SEASONAL_WAVES.map((w) => w.key);

export type OccasionPlay = "wedding_bands" | "anniversary" | "birthday" | "purchase_recall" | `upgrade_${number}y`;

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;
/** Most days a month can have. February is 29: a leap day is a real date. */
const MONTH_MAX_DAYS: readonly number[] = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// ---- input -------------------------------------------------------------------

/** An outcome labelled sold or deposit, on one thread. */
export interface OccasionSaleFact {
  readonly outcomeId: string;
  readonly threadId: string;
  readonly label: "sold" | "deposit";
  /** When it was recorded. Null or an invalid date is undated: it plans nothing. */
  readonly at: Date | null;
  /** Presence only. An amount is never carried into a proposal or printed (T3). */
  readonly amountOnRecord: boolean;
  /**
   * The `routing.decided` event that marks this sale as an engagement ring, or null. The caller decides
   * what counts (see `ringMarkerFor` in `leadQuality.ts`); the planner only needs that one exists.
   */
  readonly ringMarkerEventId: string | null;
}

/** A `lost` outcome: the one recorded sign that what came before has ended. */
export interface OccasionLostFact {
  readonly outcomeId: string;
  readonly threadId: string;
  readonly at: Date | null;
}

export interface OccasionAppointmentFact {
  readonly appointmentId: string;
  readonly status: AppointmentStatus;
  readonly scheduledAt: Date;
}

/** A `contact_memory` row, as the planner reads a key date from it. */
export interface OccasionMemoryRow {
  readonly memoryId: string;
  readonly content: string;
  readonly evidenceKind: string;
  /** Section 4's contradiction hold: shown as held, never planned from. */
  readonly held: boolean;
}

export interface OccasionFacts {
  readonly sales: readonly OccasionSaleFact[];
  readonly lost: readonly OccasionLostFact[];
  readonly appointments: readonly OccasionAppointmentFact[];
  readonly memory: readonly OccasionMemoryRow[];
  /** The contact's id when a `send_suppressions` row exists for it, else null. Presence only: the reason is never read. */
  readonly suppressedContactId: string | null;
}

// ---- output ------------------------------------------------------------------

export interface OccasionCite {
  readonly kind: "outcome" | "event" | "memory" | "appointment" | "suppression";
  readonly id: string;
  /** The table, event or evidence kind the id comes from. */
  readonly source: string;
  /** A fixed template of ids, enum values and dates: never a customer's or the shop's words, never an amount. */
  readonly note: string;
}

export interface PlannedTouch {
  readonly id: string;
  readonly play: OccasionPlay;
  /** First day of the touch, midnight UTC. */
  readonly from: Date;
  /** Last day of the touch, midnight UTC, inclusive. Equal to `from` for a single day. */
  readonly through: Date;
  /** Open: today is inside it. Upcoming: it starts after today. */
  readonly state: "open" | "upcoming";
  readonly cites: readonly OccasionCite[];
  /** The thread the anchoring sale was on; null for a contact-level key date. */
  readonly threadId: string | null;
  /** Booked or confirmed appointments that fall inside the touch's days: noted, never decisive. */
  readonly onCalendar: readonly string[];
}

export type UnplannedReason =
  | "undated"
  | "no_ring_marker"
  | "no_play"
  | "lost_after"
  | "absorbed_by_sale";

export interface UnplannedFact {
  readonly reason: UnplannedReason;
  /** The outcome or memory row that planned nothing. */
  readonly id: string;
  readonly cites: readonly OccasionCite[];
  readonly note: string;
}

export interface HeldKeyDate {
  readonly id: string;
  readonly cites: readonly OccasionCite[];
  readonly note: string;
}

/** The contact is on the send suppression list: nothing is planned, and this says so. */
export interface SuppressedContact {
  readonly cites: readonly OccasionCite[];
  readonly note: string;
}

export interface OccasionPlan {
  /** Earliest first; ties by play, then id. */
  readonly touches: readonly PlannedTouch[];
  /** Facts that planned nothing, and why. Sorted by id. */
  readonly unplanned: readonly UnplannedFact[];
  /** Key dates in section 4's contradiction hold: surfaced, never planned from. */
  readonly held: readonly HeldKeyDate[];
  /** Windows and marks wholly in the past: counted, not listed per person. */
  readonly elapsed: number;
  /** Set when the contact is suppressed; every other field is then empty. */
  readonly suppressed: SuppressedContact | null;
}

export interface SeasonalWindow {
  readonly key: SeasonalWave["key"];
  readonly label: string;
  readonly from: Date;
  readonly through: Date;
  readonly state: "open" | "upcoming";
}

// ---- dates -------------------------------------------------------------------

const validDate = (d: Date | null): d is Date => d !== null && !Number.isNaN(d.getTime());

/**
 * G19: a shop time zone the runtime does not know. Thrown, never read as UTC:
 * a wrong zone dates every touch a day off, and bad dates are worse than none.
 */
export class ShopTimeZoneUnknown extends Error {
  constructor(zone: string) {
    super(`shop time zone ${JSON.stringify(zone.slice(0, 64))} is not a time zone this runtime knows; fix clients.timezone`);
    this.name = "ShopTimeZoneUnknown";
  }
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

/** The wall clock of `at` in `zone`, the Intl pattern of attributionStamp.ts (`attributionTimeBuckets`). */
function wallClock(at: Date, zone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  let fmt = zoneFormatters.get(zone);
  if (fmt === undefined) {
    try {
      fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      });
    } catch {
      throw new ShopTimeZoneUnknown(zone);
    }
    zoneFormatters.set(zone, fmt);
  }
  const parts = fmt.formatToParts(at);
  const get = (t: string): number => Number.parseInt(parts.find((p) => p.type === t)?.value ?? "", 10);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute"), second: get("second") };
}

/**
 * G19 (AB 5.4 L4): the shop's calendar date of an instant, carried as midnight
 * UTC of that date (a date value, as every day in this file is). A null or
 * absent zone is the UTC date, the reading before `clients.timezone` existed.
 * An unknown zone throws `ShopTimeZoneUnknown`.
 */
export function shopCalendarDay(at: Date, shopTimeZone?: string | null): Date {
  if (shopTimeZone === undefined || shopTimeZone === null) return new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  const w = wallClock(at, shopTimeZone);
  return new Date(Date.UTC(w.year, w.month - 1, w.day));
}

/**
 * G19: the instant the shop's wall clock reads `hour`:00 on the shop's calendar
 * date of `at`. The zone's offset is read at that instant (twice, so a daylight
 * saving change on that date lands on the right side). A null zone is UTC.
 */
export function shopDayAtHour(at: Date, hour: number, shopTimeZone?: string | null): Date {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) throw new Error(`shopDayAtHour: hour must be an integer 0 to 23, got ${hour}`);
  return instantOnShopDate(shopCalendarDay(at, shopTimeZone), hour, shopTimeZone);
}

/**
 * G19 (TA-1261): the last instant of the shop's calendar date `day` (a date
 * value, midnight UTC of that date, as every day in this file is): one
 * millisecond before the shop's clock reads 00:00 on the next date. A null or
 * absent zone is the UTC day's end, the reading before `clients.timezone`
 * existed. The occasion calendar reads an upcoming touch's route here, the
 * least permissive instant of its first day (F-02).
 */
export function shopDayEnd(day: Date, shopTimeZone?: string | null): Date {
  return new Date(instantOnShopDate(new Date(day.getTime() + DAY_MS), 0, shopTimeZone).getTime() - 1);
}

/** The instant the shop's clock reads `hour`:00 on the shop's calendar date `day` (a date value), offset read twice across a daylight saving change. */
function instantOnShopDate(day: Date, hour: number, shopTimeZone?: string | null): Date {
  const target = day.getTime() + hour * HOURS;
  if (shopTimeZone === undefined || shopTimeZone === null) return new Date(target);
  const offsetAt = (instant: number): number => {
    const w = wallClock(new Date(instant), shopTimeZone);
    return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(instant / 1000) * 1000;
  };
  const first = target - offsetAt(target);
  return new Date(target - offsetAt(first));
}

const startOfDay = (d: Date, shopTimeZone?: string | null): Date => shopCalendarDay(d, shopTimeZone);
const utcDay = (year: number, month: number, day: number): Date => new Date(Date.UTC(year, month - 1, day));
const daysInMonth = (year: number, month: number): number => new Date(Date.UTC(year, month, 0)).getUTCDate();
export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

/** `day` moved by whole months, clamped to the last day of the month it lands in. */
function addMonths(day: Date, months: number): Date {
  const total = day.getUTCFullYear() * 12 + day.getUTCMonth() + months;
  const year = Math.floor(total / 12);
  const month = total % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day.getUTCDate(), last)));
}

/** Code-unit order, so two runs never disagree whatever the locale. */
const compare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// ---- citations ---------------------------------------------------------------

function saleCite(s: OccasionSaleFact): OccasionCite {
  const when = validDate(s.at) ? ` recorded ${isoDay(s.at)}` : " recorded with no readable date";
  return { kind: "outcome", id: s.outcomeId, source: `outcomes.${s.label}`, note: `${s.label}${when}${s.amountOnRecord ? ", amount on record" : ""}` };
}

function lostCite(l: OccasionLostFact): OccasionCite {
  const when = validDate(l.at) ? ` recorded ${isoDay(l.at)}` : "";
  return { kind: "outcome", id: l.outcomeId, source: "outcomes.lost", note: `lost${when}` };
}

function markerCite(eventId: string): OccasionCite {
  return { kind: "event", id: eventId, source: "routing.decided", note: "a routing on the thread matched an engagement-ring phrase: the only ring marker on record, weak" };
}

function memoryCite(row: OccasionMemoryRow, note: string): OccasionCite {
  return { kind: "memory", id: row.memoryId, source: row.evidenceKind, note };
}

// ---- key dates ---------------------------------------------------------------

export type ParsedKeyDate =
  | { readonly kind: "date"; readonly label: string; readonly month: number; readonly day: number }
  | { readonly kind: "undated"; readonly label: string; readonly why: string };

/** "anniversary", "birthday (her)": the shapes `decideMemorySave` stores. A label the gate never writes is not a key date. */
const KEY_DATE_ROW = /^((?:anniversary|engagement|wedding|birthday)(?: \((?:her|him|them|customer)\))?):[ \t]*(.*)$/;

/**
 * A key-date row read back, or null when the row is not one (another evidence
 * kind, or a label that is no occasion). A recognised label with no readable
 * date is `undated`: it is counted and named, never guessed. The date is read
 * by the shared month-day reader, then checked against the calendar here, since
 * that reader does not know that Apr 31 is not a day.
 */
export function parseKeyDateRow(row: OccasionMemoryRow): ParsedKeyDate | null {
  if (row.evidenceKind !== "dm.customer-claim") return null;
  const m = KEY_DATE_ROW.exec(row.content.trim());
  if (m === null) return null;
  const label = m[1] ?? "";
  // Item 153, second review: a date whose month is a "may" not written "May"
  // is a date this reader cannot read, so a row with one beside a readable
  // date ("June 3 or may 14") is two dates, never the readable one alone.
  if (extractUncertainDates(m[2] ?? "").length > 0) {
    return { kind: "undated", label, why: "a date whose month is not read (a may not written May)" };
  }
  const found = extractMonthDays(m[2] ?? "");
  const only = found.length === 1 ? found[0] : undefined;
  const parts = only === undefined ? null : /^([a-z]{3})(\d{1,2})$/.exec(only);
  if (parts === null) return { kind: "undated", label, why: "no single readable month and day" };
  const month = (MONTH_NAMES as readonly string[]).indexOf(parts[1] ?? "") + 1;
  const day = Number(parts[2]);
  if (month < 1 || day < 1 || day > (MONTH_MAX_DAYS[month - 1] ?? 0)) {
    return { kind: "undated", label, why: "not a real calendar date" };
  }
  return { kind: "date", label, month, day };
}

// ---- the plan ----------------------------------------------------------------

/**
 * `shopTimeZone` (G19, optional): the shop's IANA zone (`clients.timezone`).
 * Today, a sale's day and an appointment's day are read on the shop's calendar;
 * absent or null keeps UTC days. An unknown zone throws `ShopTimeZoneUnknown`,
 * suppressed contact or not.
 */
export function planOccasions(facts: OccasionFacts, now: Date, horizonDays: number, shopTimeZone?: string | null): OccasionPlan {
  shopCalendarDay(now, shopTimeZone);
  if (facts.suppressedContactId !== null) {
    return {
      touches: [],
      unplanned: [],
      held: [],
      elapsed: 0,
      suppressed: {
        cites: [
          {
            kind: "suppression",
            id: facts.suppressedContactId,
            source: "send_suppressions",
            note: "a suppression row exists for this contact (reason not read)",
          },
        ],
        note: "contact is on the send suppression list: nothing is planned, and no key date or sale is listed",
      },
    };
  }
  const today = startOfDay(now, shopTimeZone);
  const reach = new Date(today.getTime() + horizonDays * DAY_MS);
  const touches: PlannedTouch[] = [];
  const unplanned: UnplannedFact[] = [];
  const held: HeldKeyDate[] = [];
  let elapsed = 0;

  const appointmentsIn = (from: Date, through: Date): string[] =>
    facts.appointments
      .filter((a) => (a.status === "booked" || a.status === "confirmed") && validDate(a.scheduledAt))
      .filter((a) => {
        const d = startOfDay(a.scheduledAt, shopTimeZone).getTime();
        return d >= from.getTime() && d <= through.getTime();
      })
      .map((a) => a.appointmentId)
      .sort(compare);

  /** Adds a touch if any of its days is still ahead and it starts inside the horizon; counts it as elapsed if it is over. */
  const propose = (
    play: OccasionPlay,
    anchorId: string,
    from: Date,
    through: Date,
    cites: readonly OccasionCite[],
    threadId: string | null,
    idDay: Date = from,
  ): void => {
    if (through.getTime() < today.getTime()) {
      elapsed += 1;
      return;
    }
    if (from.getTime() > reach.getTime()) return;
    touches.push({
      id: `${play}:${anchorId}:${isoDay(idDay)}`,
      play,
      from,
      through,
      state: from.getTime() <= today.getTime() ? "open" : "upcoming",
      cites,
      threadId,
      onCalendar: appointmentsIn(from, through),
    });
  };

  // ---- ring sales: wedding bands, upgrade marks. One set per contact: see the header.
  const bySaleOrder = (a: OccasionSaleFact, b: OccasionSaleFact): number =>
    (a.at as Date).getTime() - (b.at as Date).getTime() || compare(a.outcomeId, b.outcomeId);
  const qualifying: OccasionSaleFact[] = [];
  for (const s of facts.sales) {
    if (!validDate(s.at)) {
      unplanned.push({ reason: "undated", id: s.outcomeId, cites: [saleCite(s)], note: `${s.label} has no readable date: nothing is planned from it` });
      continue;
    }
    const at = s.at.getTime();
    const lost = facts.lost
      .filter((l) => validDate(l.at) && l.threadId === s.threadId && (l.at as Date).getTime() > at)
      .sort((a, b) => (a.at as Date).getTime() - (b.at as Date).getTime() || compare(a.outcomeId, b.outcomeId))[0];
    if (lost !== undefined) {
      unplanned.push({
        reason: "lost_after",
        id: s.outcomeId,
        cites: [saleCite(s), lostCite(lost)],
        note: "lost was recorded on this thread after it: what came before has ended, so nothing is planned (bad dates are worse than no dates)",
      });
      continue;
    }
    if (s.ringMarkerEventId === null) {
      unplanned.push({
        reason: "no_ring_marker",
        id: s.outcomeId,
        cites: [saleCite(s)],
        note: "no ring marker (a routing on the thread, at or before it, that matched an engagement-ring phrase): not known to be an engagement ring, so nothing is planned from it",
      });
      continue;
    }
    qualifying.push(s);
  }
  const earliestSale = [...qualifying].filter((s) => s.label === "sold").sort(bySaleOrder)[0];
  const anchor = earliestSale ?? [...qualifying].sort(bySaleOrder)[0];
  for (const s of qualifying) {
    if (anchor === undefined || s.outcomeId === anchor.outcomeId) continue;
    unplanned.push({
      reason: "absorbed_by_sale",
      id: s.outcomeId,
      cites: [saleCite(s), saleCite(anchor)],
      note: "the contact's earliest ring sale carries the plan: one set of proposals per contact, not one per sale",
    });
  }
  if (anchor !== undefined && anchor.ringMarkerEventId !== null && validDate(anchor.at)) {
    const cites = [saleCite(anchor), markerCite(anchor.ringMarkerEventId)];
    const day = startOfDay(anchor.at, shopTimeZone);
    propose(
      "wedding_bands",
      anchor.outcomeId,
      addMonths(day, WEDDING_BANDS_WINDOW_MONTHS.from),
      addMonths(day, WEDDING_BANDS_WINDOW_MONTHS.through),
      cites,
      anchor.threadId,
    );
    if (anchor.label === "sold") {
      for (const years of UPGRADE_MARK_YEARS) {
        const mark = addMonths(day, 12 * years);
        propose(`upgrade_${years}y`, anchor.outcomeId, mark, mark, cites, anchor.threadId);
      }
    }
  }

  // ---- G18: the purchase recall, from the latest standing sale, for a contact the ring plays do not plan for.
  // The loader reads standing outcomes only (`outcomeStandsSql`), so a reversed sale is never here.
  // Open for Kabir (TA-1261 line): a ring anchor blocks the recall even when every ring play is over, so a
  // later non-ring purchase on that contact is never recalled. Pinned in the test as the logged reading.
  if (anchor === undefined) {
    const dated = facts.sales.filter((s) => s.label === "sold" && validDate(s.at)).sort(bySaleOrder);
    const latest = dated[dated.length - 1];
    if (latest !== undefined && validDate(latest.at)) {
      const at = latest.at.getTime();
      const laterLost = facts.lost
        .filter((l) => validDate(l.at) && (l.at as Date).getTime() > at)
        .sort((a, b) => (a.at as Date).getTime() - (b.at as Date).getTime() || compare(a.outcomeId, b.outcomeId))[0];
      if (laterLost !== undefined) {
        // The ring loop has already listed a same-thread lost on this sale; one line per sale and reason.
        if (!unplanned.some((u) => u.id === latest.outcomeId && u.reason === "lost_after")) {
          unplanned.push({
            reason: "lost_after",
            id: latest.outcomeId,
            cites: [saleCite(latest), lostCite(laterLost)],
            note: "lost was recorded for this contact after its latest purchase: what came before has ended, so no purchase recall is planned (bad dates are worse than no dates)",
          });
        }
      } else {
        // The purchase anchors a touch now, so it leaves the plans-nothing list (the owner's calendar
        // never lists a fact under "plans nothing" while proposing from it), and the recall's own
        // cite says why it is no ring play. Every other no-marker row still plans nothing at all.
        const listed = unplanned.findIndex((u) => u.id === latest.outcomeId && u.reason === "no_ring_marker");
        if (listed >= 0) unplanned.splice(listed, 1);
        const base = saleCite(latest);
        const cite: OccasionCite = { ...base, note: `${base.note}; no ring marker on record, so no ring play is planned from it, only the purchase recall` };
        const mark = addMonths(startOfDay(latest.at, shopTimeZone), PURCHASE_RECALL_MONTHS);
        propose("purchase_recall", latest.outcomeId, mark, mark, [cite], latest.threadId);
      }
    }
  }

  // ---- key dates: the customer's own anniversary, yearly
  for (const row of facts.memory) {
    const parsed = parseKeyDateRow(row);
    if (parsed === null) continue;
    if (row.held) {
      held.push({
        id: row.memoryId,
        cites: [memoryCite(row, `held ${parsed.label} key date`)],
        note: `${parsed.label} key date is held (two sources disagree): shown, never planned from`,
      });
      continue;
    }
    if (parsed.kind === "undated") {
      unplanned.push({
        reason: "undated",
        id: row.memoryId,
        cites: [memoryCite(row, `${parsed.label} key date, ${parsed.why}`)],
        note: `${parsed.label} key date: ${parsed.why}, so nothing is planned from it`,
      });
      continue;
    }
    const isBirthday = BIRTHDAY_LABELS.includes(parsed.label);
    if (parsed.label !== ANNIVERSARY_LABEL && !isBirthday) {
      unplanned.push({
        reason: "no_play",
        id: row.memoryId,
        cites: [memoryCite(row, `${parsed.label} key date`)],
        note: `${parsed.label} is not a date a play stands for (the customer's own anniversary and stated birthdays are): no play stands for it, and a wedding date is never guessed`,
      });
      continue;
    }
    const play: OccasionPlay = isBirthday ? "birthday" : "anniversary";
    const lead = isBirthday ? BIRTHDAY_LEAD_DAYS * DAY_MS : 0;
    const cites = [memoryCite(row, `customer-stated ${play}, live row`)];
    for (let year = today.getUTCFullYear(); year <= reach.getUTCFullYear(); year++) {
      const date = utcDay(year, parsed.month, Math.min(parsed.day, daysInMonth(year, parsed.month)));
      if (date.getTime() < today.getTime() || date.getTime() - lead > reach.getTime()) continue;
      propose(play, row.memoryId, new Date(date.getTime() - lead), date, cites, null, date);
    }
  }

  touches.sort((a, b) => a.from.getTime() - b.from.getTime() || compare(a.play, b.play) || compare(a.id, b.id));
  unplanned.sort((a, b) => compare(a.id, b.id) || compare(a.reason, b.reason));
  held.sort((a, b) => compare(a.id, b.id));
  return { touches, unplanned, held, elapsed, suppressed: null };
}

/** The earliest proposal not yet over: what the call list reads as the contact's next occasion. */
export function nextTouch(plan: OccasionPlan): PlannedTouch | null {
  return plan.touches[0] ?? null;
}

/** The `nth` `weekday` of `month` in `year`, as a UTC day. */
function nthWeekdayOf(year: number, t: WaveNthWeekday): Date {
  const first = utcDay(year, t.month, 1);
  const shift = (t.weekday - first.getUTCDay() + 7) % 7;
  return utcDay(year, t.month, 1 + shift + (t.nth - 1) * 7);
}

/** A wave's window for the year its `from` falls in (a wrapping window ends in the next year). */
function waveDates(wave: SeasonalWave, year: number): { from: Date; through: Date } {
  const t = wave.through;
  if ("daysBefore" in wave.from) {
    const through = "weekday" in t ? nthWeekdayOf(year, t) : utcDay(year, t.month, t.day);
    return { from: new Date(through.getTime() - wave.from.daysBefore * DAY_MS), through };
  }
  const from = utcDay(year, wave.from.month, wave.from.day);
  const throughYear = t.month < wave.from.month ? year + 1 : year;
  return { from, through: "weekday" in t ? nthWeekdayOf(throughYear, t) : utcDay(throughYear, t.month, t.day) };
}

const ORDINALS = ["first", "second", "third", "fourth", "fifth"] as const;
const WEEKDAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

/** A wave's window as words, for the provisional summary. */
function waveRule(wave: SeasonalWave): string {
  const t = wave.through;
  const through = "weekday" in t ? `the ${ORDINALS[t.nth - 1] ?? "?"} ${WEEKDAY_LABELS[t.weekday] ?? "?"} of ${MONTH_LONG[t.month - 1] ?? "?"}` : monthDay(t);
  const from = "daysBefore" in wave.from ? `${wave.from.daysBefore} days before` : `${monthDay(wave.from)} to`;
  return `${wave.label} ${from} ${"daysBefore" in wave.from ? "through " : ""}${through}`;
}

/**
 * Seasonal windows overlapping today through `days` days ahead. A calendar: no person is in it.
 * G19 (TA-1296 final review, core-7): today is the shop's calendar day when `shopTimeZone` is
 * given (the same `shopCalendarDay` read as every per-person touch), the UTC day without one, so a
 * wave's open or upcoming state flips on the same day the touches beside it do. An unknown zone
 * throws `ShopTimeZoneUnknown`, never reads as UTC.
 */
export function seasonalWindows(now: Date, days: number, shopTimeZone?: string | null): SeasonalWindow[] {
  const today = startOfDay(now, shopTimeZone);
  const reach = new Date(today.getTime() + days * DAY_MS);
  const out: SeasonalWindow[] = [];
  for (const wave of SEASONAL_WAVES) {
    for (let year = today.getUTCFullYear() - 1; year <= reach.getUTCFullYear(); year++) {
      const { from, through } = waveDates(wave, year);
      if (through.getTime() < today.getTime() || from.getTime() > reach.getTime()) continue;
      out.push({ key: wave.key, label: wave.label, from, through, state: from.getTime() <= today.getTime() ? "open" : "upcoming" });
    }
  }
  return out.sort((a, b) => a.from.getTime() - b.from.getTime() || compare(a.key, b.key));
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;
const monthDay = (p: WaveMonthDay): string => `${MONTH_LABELS[p.month - 1] ?? "?"} ${p.day}`;

/** Every named interval and window, as report lines: a date is never printed without the rule behind it. */
export function provisionalSummary(): string[] {
  return [
    `${OCCASION_RATIFICATION} (Kabir): every interval and window below is provisional.`,
    `  wedding bands: ${WEDDING_BANDS_WINDOW_MONTHS.from} to ${WEDDING_BANDS_WINDOW_MONTHS.through} months after an engagement-ring sale or deposit (a ring marker is required; one set per contact, from its earliest sale)`,
    `  ring marker: a routing on the sale's thread, at or before the sale, that matched ${RING_MARKER_PHRASES.map((p) => `"${p}"`).join(" or ")}; custom_engagement alone is not one`,
    `  anniversary: yearly on the date the customer stated for their own anniversary`,
    `  birthday: yearly, ${BIRTHDAY_LEAD_DAYS} days before through the date the customer stated for a birthday (their own or a named partner's), a lead so a gift can still be ordered (TA-1196 rec); never a date from any other source`,
    `  upgrade: ${UPGRADE_MARK_YEARS.join(", ")} years after an engagement-ring sale (a deposit is not a sale)`,
    `  purchase recall: ${PURCHASE_RECALL_MONTHS} months after the contact's latest purchase (a sold outcome), one day, only when no ring play plans for the contact; a later lost cancels it`,
    `  seasonal waves, a calendar only and never per person: ${SEASONAL_WAVES.map(waveRule).join("; ")}`,
  ];
}
